<template>
  <div class="registration-dashboard">
    <transition name="fade">
      <div
        v-if="showNotificationFlag"
        class="notification"
        :class="notificationType"
      >
        {{ notificationMessage }}
      </div>
    </transition>

    <!-- Dashboard View -->
    <div v-if="currentView === 'dashboard'">
      <!-- Registration Forms Display -->
      <div v-if="activeTab === 'forms'">
        <FormsList
          :registration-forms="registrationForms"
          @create="createForm"
          @edit="editForm"
          @copy="copyForm"
          @delete="deleteRegistrationForm"
          @show-embed="showEmbedCode"
          @view="viewForm"
        />
      </div>

      <!-- Competition List Display -->
      <div v-if="activeTab === 'competitions'">
        <CompetitionList />
      </div>

      <!-- Cup List Display -->
      <div v-if="activeTab === 'cups'">
        <CupList />
      </div>

      <div v-if="activeTab === 'embed-scripts'">
        <EmbedScriptsList />
      </div>

      <!-- Embed Code Modal -->
      <transition name="fade">
        <div v-if="showEmbed" class="modal-overlay" @click="closeEmbedModal">
          <div class="modal-content embed-modal-content" @click.stop>
            <EmbedCode
              :registrationForm="selectedRegistrationForm"
              @close="closeEmbedModal"
              @copy-embed-code="copyEmbedCode($event)"
            />
          </div>
        </div>
      </transition>

      <!-- Form Preview Modal -->
      <transition name="fade">
        <div
          v-if="showPreview"
          class="modal-overlay"
          @click="closePreviewModal"
        >
          <div class="modal-content form-preview" @click.stop>
            <div class="preview-header">
              <h3>{{ t('registration.formPreview') }}</h3>
              <button @click="closePreviewModal" class="close-btn">
                &times;
              </button>
            </div>
            <div class="preview-content">
              <div v-if="previewForm" class="form-preview-container">
                <h2>{{ previewForm.title }}</h2>
                <p v-if="previewForm.description">
                  {{ previewForm.description }}
                </p>
                <div
                  class="field-preview"
                  v-for="field in previewForm.fields"
                  :key="field.id"
                >
                  <label class="field-label">
                    {{ field.label }}
                    <span v-if="field.required" class="required">*</span>
                  </label>
                  <div class="field-type">
                    {{ getFieldTypeLabel(field.type) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Form Builder Full Page -->
    <div v-if="currentView === 'formBuilder'" class="form-builder-page">
      <FormBuilder
        :form="editingForm"
        @close="closeFormBuilder"
        @save="saveForm"
      />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import EmbedCode from './UI/Registrations/EmbedCode.vue'
import CompetitionList from './UI/Registrations/CompetitionList.vue'
import FormBuilder from './UI/Registrations/FormBuilder.vue'
import CupList from './UI/Registrations/CupList.vue'
import EmbedScriptsList from './UI/Registrations/EmbedScriptsList.vue'
import FormsList from './UI/Registrations/FormsList.vue'
import { useNotification } from '@/composables/useNotification'
import { useI18n } from 'vue-i18n'

export default {
  components: {
    EmbedCode,
    CompetitionList,
    FormBuilder,
    CupList,
    EmbedScriptsList,
    FormsList,
  },
  setup() {
    const route = useRoute()

    const tabFromRouteName = (name) => {
      const map = {
        RegistrationCompetitions: 'competitions',
        RegistrationCups: 'cups',
        RegistrationForms: 'forms',
        RegistrationEmbedScripts: 'embed-scripts',
      }
      return map[name] || 'competitions'
    }

    const activeTab = ref(tabFromRouteName(route.name))
    const currentView = ref('dashboard') // 'dashboard' or 'formBuilder'
    const registrationForms = ref([])
    const showEmbed = ref(false)
    const showPreview = ref(false)
    const selectedRegistrationForm = ref(null)
    const previewForm = ref(null)
    const editingForm = ref(null)
    const { t } = useI18n()

    const {
      showNotificationFlag,
      notificationMessage,
      notificationType,
      showNotification,
    } = useNotification()

    // Fetch registration forms from API
    const fetchRegistrationForms = async () => {
      try {
        const response = await axios.get('/registration/forms')
        registrationForms.value = response.data
          .map((form) => ({
            id: form.id,
            name: form.name,
            title: form.title,
            description: form.description,
            fields: Array.isArray(form.fields)
              ? form.fields
              : JSON.parse(form.fields || '[]'),
            styling:
              typeof form.styling === 'object'
                ? form.styling
                : JSON.parse(form.styling || '{}'),
            settings:
              typeof form.settings === 'object'
                ? form.settings
                : JSON.parse(form.settings || '{}'),
            translations:
              typeof form.translations === 'object'
                ? form.translations
                : JSON.parse(form.translations || '{}'),
            isActive: form.is_active,
            submissionCount: form.submission_count || 0,
            organizerId: form.organizer_id,
            createdAt: form.created_at,
            updatedAt: form.updated_at,
          }))
          .sort((a, b) => b.id - a.id) // Sort based on id
      } catch (error) {
        console.error('Error fetching registration forms:', error)
        showNotification(
          'Failed to fetch registration forms. Please try again later.',
          'error'
        )
      }
    }

    // Delete registration form from API
    const deleteRegistrationForm = async (formId) => {
      if (confirm(t('registration.deleteFormConfirm'))) {
        try {
          await axios.delete(`/registration/form/${formId}`)
          await fetchRegistrationForms()
          showNotification('Registration form deleted successfully!', 'success')
        } catch (error) {
          console.error('Error deleting registration form:', error)
          showNotification(
            'Failed to delete registration form. Please try again.',
            'error'
          )
        }
      }
    }

    // View form in preview modal
    const viewForm = (form) => {
      previewForm.value = form
      showPreview.value = true
    }

    // Show embed code modal
    const showEmbedCode = (form) => {
      selectedRegistrationForm.value = form
      showEmbed.value = true
    }

    // Close embed code modal
    const closeEmbedModal = () => {
      showEmbed.value = false
    }

    // Close preview modal
    const closePreviewModal = () => {
      showPreview.value = false
      previewForm.value = null
    }

    // Copy embed code to clipboard
    const copyEmbedCode = async (embedCodeText) => {
      try {
        await navigator.clipboard.writeText(embedCodeText)
        showNotification('Embed code copied to clipboard!', 'success')
        closeEmbedModal()
      } catch (err) {
        console.error('Error copying embed code:', err)
        showNotification('Failed to copy embed code.', 'error')
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return t('general.notSet')
      const date = new Date(dateString)
      return date.toLocaleDateString('et-EE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    }

    // Get field type label
    const getFieldTypeLabel = (type) => {
      const typeLabels = {
        text: 'Text Input',
        email: 'Email',
        tel: 'Phone',
        number: 'Number',
        textarea: 'Text Area',
        select: 'Dropdown',
        radio: 'Radio Buttons',
        checkbox: 'Checkboxes',
        date: 'Date',
        time: 'Time',
        file: 'File Upload',
        url: 'URL',
        password: 'Password',
      }
      return typeLabels[type] || type
    }

    // Form management methods
    const editForm = (form) => {
      editingForm.value = { ...form }
      currentView.value = 'formBuilder'
    }

    const createForm = () => {
      editingForm.value = null
      currentView.value = 'formBuilder'
    }

    const closeFormBuilder = () => {
      currentView.value = 'dashboard'
      editingForm.value = null
    }

    const saveForm = async (formData) => {
      try {
        if (editingForm.value && editingForm.value.id) {
          // Update existing form
          await axios.put(
            `/registration/form/${editingForm.value.id}`,
            formData
          )
          showNotification('Form updated successfully!', 'success')
        } else {
          // Create new form
          await axios.post('/registration/form', formData)
          showNotification('Form created successfully!', 'success')
        }

        await fetchRegistrationForms()
        closeFormBuilder()
      } catch (error) {
        console.error('Error saving form:', error)
        showNotification('Failed to save form. Please try again.', 'error')
      }
    }

    // Copy an existing form's configuration into a new unsaved form
    const copyForm = (form) => {
      // Only copy settings relevant to building a new form; do not keep id or metadata
      const clone = {
        name: `${form.name || form.title || 'Form'} Copy`,
        title: `${form.title || form.name || 'Form'} (Copy)`,
        description: form.description || '',
        isActive: false,
        fields: Array.isArray(form.fields)
          ? JSON.parse(JSON.stringify(form.fields))
          : [],
        styling:
          typeof form.styling === 'object'
            ? JSON.parse(JSON.stringify(form.styling))
            : {},
        settings:
          typeof form.settings === 'object'
            ? JSON.parse(JSON.stringify(form.settings))
            : {},
        translations:
          typeof form.translations === 'object'
            ? JSON.parse(JSON.stringify(form.translations))
            : {},
      }

      // Open in builder; it is not saved until user clicks Save
      editingForm.value = clone
      currentView.value = 'formBuilder'
    }

    watch(
      () => route.name,
      (name) => {
        if (currentView.value === 'dashboard') {
          activeTab.value = tabFromRouteName(name)
        }
      }
    )

    // Initialize by fetching registration forms
    onMounted(() => {
      activeTab.value = tabFromRouteName(route.name)
      fetchRegistrationForms()
    })

    return {
      activeTab,
      currentView,
      registrationForms,
      showEmbed,
      showPreview,
      selectedRegistrationForm,
      previewForm,
      editingForm,
      showEmbedCode,
      closeEmbedModal,
      closePreviewModal,
      copyEmbedCode,
      deleteRegistrationForm,
      viewForm,
      formatDate,
      getFieldTypeLabel,
      editForm,
      createForm,
      closeFormBuilder,
      saveForm,
      showNotificationFlag,
      notificationMessage,
      notificationType,
      copyForm,
      t,
    }
  },
}
</script>

<style scoped>
/* Registration dashboard aligned to Results style */
.registration-dashboard {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  box-sizing: border-box;
}

.registration-dashboard > div:not(.notification) {
  width: 100%;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  max-width: 90%;
  max-height: 90%;
  overflow-y: auto;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
}

.embed-modal-content {
  width: min(900px, 95vw);
  max-width: min(900px, 95vw);
  padding: 0;
  overflow: hidden;
}

.form-preview {
  width: 600px;
  max-width: 90vw;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.preview-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.2rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.6rem;
  cursor: pointer;
  color: #6c757d;
  line-height: 1;
  padding: 0;
}

.close-btn:hover {
  color: #495057;
}

.preview-content {
  padding: 1rem;
}

.form-preview-container h2 {
  color: #2c3e50;
  margin-bottom: 1rem;
}

.form-preview-container p {
  color: #6c757d;
  margin-bottom: 1.2rem;
}

.field-preview {
  margin-bottom: 0.75rem;
  padding: 0.7rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #3498db;
}

.field-label {
  display: block;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.4rem;
}

.required {
  color: #dc3545;
}

.field-type {
  font-size: 0.85rem;
  color: #6c757d;
}

.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  display: inline-block;
  padding: 15px 20px;
  border-radius: 4px;
  color: #fff;
  font-weight: 700;
  width: auto;
  max-width: calc(100vw - 40px);
  line-height: 1.35;
  white-space: normal;
  overflow-wrap: anywhere;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.2);
  z-index: 3000;
  transition: opacity 0.3s ease;
}

.notification.success {
  background-color: #2ecc71;
}

.notification.error {
  background-color: #e74c3c;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.form-builder-page {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #fff;
  z-index: 2000;
  overflow: hidden;
}

@media (max-width: 768px) {
  .registration-dashboard {
    padding: 0.75rem;
  }

  .tab-container {
    gap: 0.5rem;
  }

  .tab-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
